import React from 'react';
import {
  Accordion, AccordionDetails, AccordionSummary, Box, Checkbox, FormControl,
  FormControlLabel, FormLabel, Grid, Radio, RadioGroup, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import type {
  PhysioInitialResponses,
  PhysioReEvaluationResponses,
  PhysiotherapyAssessmentType,
  TriStateResult,
} from '../../types/physiotherapyAssessment';
import {
  PHYSIO_CONDUCT_OPTIONS,
  PHYSIO_INITIAL_SECTIONS,
  PHYSIO_REEVAL_SECTIONS,
  PHYSIO_SPECIFIC_GOALS,
  PHYSIO_THERAPY_MODALITIES,
  applyReEvalAnthropometryCurrent,
  computeAgeFromBirthDate,
  recomputeAnthropometryDerived,
  recomputeComparisonRows,
  recomputeIdentificationDerived,
} from '../../constants/physiotherapyAssessmentSchema';
import { useClinicPageBreakpoints } from '../../hooks/useClinicPageBreakpoints';
import { PATIENT_PANEL_THEME } from '../patientPanelTheme';
import PosturalAssessmentSection from './PosturalAssessmentSection';

const fieldSx = {
  '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff' },
};

const dateFieldSx = {
  ...fieldSx,
  '& .MuiInputBase-input': {
    fontSize: { xs: '16px', sm: '0.875rem' },
    lineHeight: 1.4,
    minHeight: { xs: 40, sm: 'auto' },
  },
};

const readOnlyContainerSx = {
  '& .MuiOutlinedInput-root': { bgcolor: '#f8fafc' },
  '& .MuiInputBase-input, & .MuiInputBase-inputMultiline': { pointerEvents: 'none', userSelect: 'text' },
  '& .MuiCheckbox-root, & .MuiRadio-root': { pointerEvents: 'none' },
  '& .MuiFormControlLabel-root': { cursor: 'default' },
};

const sectionAccordionSx = (isMobile: boolean) => ({
  borderRadius: '12px !important',
  border: '1px solid #e2e8f0',
  boxShadow: 'none',
  '&:before': { display: 'none' },
  mb: 1,
  '&.Mui-expanded': { mb: 1 },
  ...(isMobile ? {
    overflow: 'visible',
    '& .MuiCollapse-root': { overflow: 'visible !important' },
    '& .MuiAccordionDetails-root': { overflow: 'visible !important' },
  } : {}),
});

const sectionDetailsSx = (isMobile: boolean) => ({
  pt: isMobile ? 1.5 : 0,
  pb: isMobile ? 2 : 1,
  overflow: 'visible',
});

const mobileFormContainerSx = (isMobile: boolean) => (
  isMobile
    ? {
      overflow: 'visible',
      '& .MuiTextField-root, & .MuiFormControl-root, & .MuiGrid-item': { overflow: 'visible' },
      '& .MuiInputLabel-root': { backgroundColor: '#f8fafc', px: 0.25 },
      '& .MuiTextField-root': { mt: 0.25 },
    }
    : undefined
);

const sectionSummarySx = {
  minHeight: 48,
  '& .MuiAccordionSummary-content': { my: 1 },
};

function ScrollTable({
  children,
  minWidth = 520,
}: {
  children: React.ReactNode;
  minWidth?: number;
}) {
  return (
    <TableContainer
      sx={{
        overflowX: 'auto',
        WebkitOverflowScrolling: 'touch',
        width: '100%',
        maxWidth: '100%',
        mb: 0.5,
      }}
    >
      <Table size="small" sx={{ minWidth }}>{children}</Table>
    </TableContainer>
  );
}

function TriStateRadio({
  value,
  onChange,
  label,
  isMobile,
}: {
  value: TriStateResult;
  onChange: (v: TriStateResult) => void;
  label: string;
  isMobile: boolean;
}) {
  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569', display: 'block', mb: 0.5 }}>
        {label}
      </Typography>
      <RadioGroup row={!isMobile} value={value} onChange={(e) => onChange(e.target.value as TriStateResult)}>
        <FormControlLabel value="normal" control={<Radio size="small" />} label="Normal" />
        <FormControlLabel value="altered" control={<Radio size="small" />} label="Alterado" />
        <FormControlLabel value="not_done" control={<Radio size="small" />} label="Não realizado" />
      </RadioGroup>
    </Box>
  );
}

function InitialForm({
  value,
  onChange,
  patientId,
  readOnly = false,
  isMobile,
}: {
  value: PhysioInitialResponses;
  onChange: (next: PhysioInitialResponses) => void;
  patientId?: string;
  readOnly?: boolean;
  isMobile: boolean;
}) {
  const applyChange = (next: PhysioInitialResponses) => {
    if (!readOnly) onChange(next);
  };
  const setId = (patch: Partial<PhysioInitialResponses['identification']>) => {
    const merged = { ...value.identification, ...patch };
    if (patch.birth_date !== undefined) {
      merged.age = computeAgeFromBirthDate(merged.birth_date) || merged.age;
    }
    const identification = recomputeIdentificationDerived(merged);
    const anthropometry = recomputeAnthropometryDerived(
      value.anthropometry,
      identification.gender,
      identification.age,
    );
    applyChange({ ...value, identification, anthropometry });
  };

  const setAnthropometry = (patch: Partial<PhysioInitialResponses['anthropometry']>) => {
    const anthropometry = recomputeAnthropometryDerived(
      { ...value.anthropometry, ...patch },
      value.identification.gender,
      value.identification.age,
    );
    applyChange({ ...value, anthropometry });
  };

  const toggleArray = (arr: string[], key: string) =>
    arr.includes(key) ? arr.filter((k) => k !== key) : [...arr, key];

  return (
    <Box sx={{ ...(readOnly ? readOnlyContainerSx : {}), ...mobileFormContainerSx(isMobile) }}>
      {PHYSIO_INITIAL_SECTIONS.map((section) => (
        <Accordion key={section.key} defaultExpanded={section.key === 'identification'} sx={sectionAccordionSx(isMobile)}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={sectionSummarySx}>
            <Typography sx={{ fontWeight: 700, color: PATIENT_PANEL_THEME.sectionTitle, fontSize: '0.9rem' }}>
              {section.title}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={sectionDetailsSx(isMobile)}>
            {section.key === 'identification' && (
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" label="Nome completo" value={value.identification.full_name}
                    onChange={(e) => setId({ full_name: e.target.value })} sx={fieldSx} />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField fullWidth size="small" type="date" label="Data de nascimento" InputLabelProps={{ shrink: true }}
                    value={value.identification.birth_date} onChange={(e) => setId({ birth_date: e.target.value })} sx={dateFieldSx} />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField fullWidth size="small" label="Idade" value={value.identification.age}
                    onChange={(e) => setId({ age: e.target.value })} sx={fieldSx} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl>
                    <FormLabel sx={{ fontSize: '0.75rem', color: '#64748b' }}>Sexo</FormLabel>
                    <RadioGroup row={!isMobile} value={value.identification.gender} onChange={(e) => setId({ gender: e.target.value as 'M' | 'F' | '' })}>
                      <FormControlLabel value="M" control={<Radio size="small" />} label="M" />
                      <FormControlLabel value="F" control={<Radio size="small" />} label="F" />
                    </RadioGroup>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth size="small" label="Profissão" value={value.identification.profession}
                    onChange={(e) => setId({ profession: e.target.value })} sx={fieldSx} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth size="small" label="Telefone" value={value.identification.phone}
                    onChange={(e) => setId({ phone: e.target.value })} sx={fieldSx} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth size="small" label="Peso (kg)" value={value.identification.weight_kg}
                    onChange={(e) => setId({ weight_kg: e.target.value })} sx={fieldSx} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth size="small" label="Altura (m)" value={value.identification.height_m}
                    onChange={(e) => setId({ height_m: e.target.value })} sx={fieldSx} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth size="small" label="IMC" value={value.identification.imc} InputProps={{ readOnly: true }} sx={fieldSx} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth size="small" label="Classificação IMC" value={value.identification.imc_classification}
                    InputProps={{ readOnly: true }} sx={fieldSx} />
                </Grid>
              </Grid>
            )}

            {section.key === 'anthropometry' && (
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth size="small" label="Circunferência abdominal (cm)" value={value.anthropometry.abdominal_circ_cm}
                    onChange={(e) => setAnthropometry({ abdominal_circ_cm: e.target.value })} sx={fieldSx} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth size="small" label="Circunferência da cintura (cm)" value={value.anthropometry.waist_circ_cm}
                    onChange={(e) => setAnthropometry({ waist_circ_cm: e.target.value })} sx={fieldSx} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth size="small" label="Circunferência do quadril (cm)" value={value.anthropometry.hip_circ_cm}
                    onChange={(e) => setAnthropometry({ hip_circ_cm: e.target.value })} sx={fieldSx} />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField fullWidth size="small" label="RCQ (cintura/quadril)" value={value.anthropometry.waist_hip_ratio}
                    InputProps={{ readOnly: true }} sx={fieldSx} />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField fullWidth size="small" label="Risco RCQ" value={value.anthropometry.waist_hip_risk}
                    InputProps={{ readOnly: true }} sx={fieldSx} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" label="Risco metabólico (cintura)" value={value.anthropometry.waist_risk}
                    InputProps={{ readOnly: true }} sx={fieldSx} />
                </Grid>
                <Grid item xs={12}>
                  <ScrollTable minWidth={280}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Local</TableCell>
                        <TableCell width={120}>Resultado (mm)</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {value.anthropometry.skinfolds.map((row, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{row.location}</TableCell>
                          <TableCell>
                            <TextField fullWidth size="small" value={row.result_mm}
                              onChange={(e) => {
                                const skinfolds = [...value.anthropometry.skinfolds];
                                skinfolds[idx] = { ...row, result_mm: e.target.value };
                                setAnthropometry({ skinfolds });
                              }} sx={fieldSx} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </ScrollTable>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField fullWidth size="small" label="Σ pregas cutâneas (mm)" value={value.anthropometry.skinfold_sum_mm}
                    InputProps={{ readOnly: true }} sx={fieldSx} />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField fullWidth size="small" label="% gordura (estimativa)" value={value.anthropometry.body_fat_percent}
                    helperText={value.identification.gender === 'F'
                      ? 'JP 3 dobras: tríceps, supra-ilíaca e coxa'
                      : 'JP 3 dobras: abdominal, tríceps e coxa'}
                    InputProps={{ readOnly: true }} sx={fieldSx} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth multiline minRows={2} size="small" label="Observações" value={value.anthropometry.notes}
                    onChange={(e) => setAnthropometry({ notes: e.target.value })} sx={fieldSx} />
                </Grid>
              </Grid>
            )}

            {section.key === 'complaint' && (
              <Grid container spacing={1.5}>
                <Grid item xs={12}>
                  <TextField fullWidth multiline minRows={2} size="small" label="Queixa principal" value={value.complaint.main_complaint}
                    onChange={(e) => onChange({ ...value, complaint: { ...value.complaint, main_complaint: e.target.value } })} sx={fieldSx} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth multiline minRows={2} size="small" label="Objetivo principal do tratamento" value={value.complaint.main_goal}
                    onChange={(e) => onChange({ ...value, complaint: { ...value.complaint, main_goal: e.target.value } })} sx={fieldSx} />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569' }}>Objetivos específicos</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                    {PHYSIO_SPECIFIC_GOALS.map((g) => (
                      <FormControlLabel key={g.key}
                        control={<Checkbox size="small" checked={value.complaint.specific_goals.includes(g.key)}
                          onChange={() => onChange({ ...value, complaint: { ...value.complaint, specific_goals: toggleArray(value.complaint.specific_goals, g.key) } })} />}
                        label={<Typography variant="caption">{g.label}</Typography>}
                      />
                    ))}
                  </Box>
                  {value.complaint.specific_goals.includes('other') && (
                    <TextField fullWidth size="small" label="Outro objetivo" sx={{ mt: 1, ...fieldSx }} value={value.complaint.specific_goal_other}
                      onChange={(e) => onChange({ ...value, complaint: { ...value.complaint, specific_goal_other: e.target.value } })} />
                  )}
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569' }}>Tarefas/atividades com dificuldade</Typography>
                  {value.complaint.difficult_tasks.map((task, idx) => (
                    <TextField key={idx} fullWidth size="small" sx={{ mt: 1, ...fieldSx }} placeholder={`${idx + 1}.`}
                      value={task} onChange={(e) => {
                        const difficult_tasks = [...value.complaint.difficult_tasks];
                        difficult_tasks[idx] = e.target.value;
                        onChange({ ...value, complaint: { ...value.complaint, difficult_tasks } });
                      }} />
                  ))}
                </Grid>
              </Grid>
            )}

            {section.key === 'clinical_history' && (
              <Grid container spacing={1.5}>
                {([
                  ['current_illness', 'História da moléstia atual'],
                  ['past_history', 'História pregressa / condições associadas'],
                  ['prior_surgeries', 'Cirurgias prévias'],
                  ['prior_injuries', 'Lesões/traumas anteriores relevantes'],
                  ['complementary_exams', 'Exames complementares relevantes'],
                ] as const).map(([key, label]) => (
                  <Grid item xs={12} key={key}>
                    <TextField fullWidth multiline minRows={2} size="small" label={label}
                      value={value.clinical_history[key]}
                      onChange={(e) => onChange({ ...value, clinical_history: { ...value.clinical_history, [key]: e.target.value } })}
                      sx={fieldSx} />
                  </Grid>
                ))}
              </Grid>
            )}

            {section.key === 'medications' && (
              <ScrollTable minWidth={560}>
                <TableHead>
                  <TableRow>
                    <TableCell>Medicamento</TableCell>
                    <TableCell>Dosagem</TableCell>
                    <TableCell>Frequência</TableCell>
                    <TableCell>Finalidade</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {value.medications.map((row, idx) => (
                    <TableRow key={idx}>
                      {(['medication', 'dosage', 'frequency', 'purpose'] as const).map((col) => (
                        <TableCell key={col}>
                          <TextField fullWidth size="small" value={row[col]}
                            onChange={(e) => {
                              const medications = [...value.medications];
                              medications[idx] = { ...row, [col]: e.target.value };
                              onChange({ ...value, medications });
                            }} sx={fieldSx} />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </ScrollTable>
            )}

            {section.key === 'vital_signs' && (
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={3}>
                  <TextField fullWidth size="small" label="Pressão arterial (mmHg)" value={value.vital_signs.blood_pressure}
                    onChange={(e) => onChange({ ...value, vital_signs: { ...value.vital_signs, blood_pressure: e.target.value } })} sx={fieldSx} />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField fullWidth size="small" label="FC (bpm)" value={value.vital_signs.heart_rate}
                    onChange={(e) => onChange({ ...value, vital_signs: { ...value.vital_signs, heart_rate: e.target.value } })} sx={fieldSx} />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField fullWidth size="small" label="SpO₂ (%)" value={value.vital_signs.spo2}
                    onChange={(e) => onChange({ ...value, vital_signs: { ...value.vital_signs, spo2: e.target.value } })} sx={fieldSx} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" label="Ausculta" value={value.vital_signs.auscultation}
                    onChange={(e) => onChange({ ...value, vital_signs: { ...value.vital_signs, auscultation: e.target.value } })} sx={fieldSx} />
                </Grid>
              </Grid>
            )}

            {section.key === 'pain' && (
              <Grid container spacing={1.5}>
                <Grid item xs={12}>
                  <FormControl>
                    <FormLabel sx={{ fontSize: '0.75rem' }}>Presença de dor</FormLabel>
                    <RadioGroup row={!isMobile} value={value.pain.has_pain} onChange={(e) => onChange({ ...value, pain: { ...value.pain, has_pain: e.target.value as 'yes' | 'no' | '' } })}>
                      <FormControlLabel value="no" control={<Radio size="small" />} label="Não" />
                      <FormControlLabel value="yes" control={<Radio size="small" />} label="Sim" />
                    </RadioGroup>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField fullWidth size="small" label="Intensidade END (0–10)" value={value.pain.intensity_end}
                    onChange={(e) => onChange({ ...value, pain: { ...value.pain, intensity_end: e.target.value } })} sx={fieldSx} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth size="small" label="Localização" value={value.pain.location}
                    onChange={(e) => onChange({ ...value, pain: { ...value.pain, location: e.target.value } })} sx={fieldSx} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth multiline minRows={2} size="small" label="Característica / comportamento" value={value.pain.characteristics}
                    onChange={(e) => onChange({ ...value, pain: { ...value.pain, characteristics: e.target.value } })} sx={fieldSx} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth multiline minRows={2} size="small" label="Observações" value={value.pain.notes}
                    onChange={(e) => onChange({ ...value, pain: { ...value.pain, notes: e.target.value } })} sx={fieldSx} />
                </Grid>
              </Grid>
            )}

            {section.key === 'mobility' && (
              <Box>
                <FormControl sx={{ mb: 2 }}>
                  <FormLabel sx={{ fontSize: '0.75rem' }}>ADM geral</FormLabel>
                  <RadioGroup row={!isMobile} value={value.mobility.general_adm}
                    onChange={(e) => onChange({ ...value, mobility: { ...value.mobility, general_adm: e.target.value as PhysioInitialResponses['mobility']['general_adm'] } })}>
                    <FormControlLabel value="preserved" control={<Radio size="small" />} label="Preservada" />
                    <FormControlLabel value="reduced" control={<Radio size="small" />} label="Reduzida" />
                    <FormControlLabel value="increased" control={<Radio size="small" />} label="Aumentada" />
                    <FormControlLabel value="not_evaluated" control={<Radio size="small" />} label="Não avaliada" />
                  </RadioGroup>
                </FormControl>
                <ScrollTable minWidth={680}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Articulação/segmento</TableCell>
                      <TableCell>Movimento</TableCell>
                      <TableCell width={70}>D</TableCell>
                      <TableCell width={70}>E</TableCell>
                      <TableCell>Observações</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {value.mobility.rows.map((row, idx) => (
                      <TableRow key={idx}>
                        {(['joint_segment', 'movement', 'right', 'left', 'notes'] as const).map((col) => (
                          <TableCell key={col}>
                            <TextField fullWidth size="small" value={row[col]}
                              onChange={(e) => {
                                const rows = [...value.mobility.rows];
                                rows[idx] = { ...row, [col]: e.target.value };
                                onChange({ ...value, mobility: { ...value.mobility, rows } });
                              }} sx={fieldSx} />
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </ScrollTable>
              </Box>
            )}

            {section.key === 'strength' && (
              <ScrollTable minWidth={480}>
                <TableHead>
                  <TableRow>
                    <TableCell>Grupo muscular</TableCell>
                    <TableCell width={70}>D</TableCell>
                    <TableCell width={70}>E</TableCell>
                    <TableCell>Observações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {value.strength.rows.map((row, idx) => (
                    <TableRow key={idx}>
                      <TableCell>
                        <TextField fullWidth size="small" value={row.muscle_group}
                          onChange={(e) => {
                            const rows = [...value.strength.rows];
                            rows[idx] = { ...row, muscle_group: e.target.value };
                            onChange({ ...value, strength: { ...value.strength, rows } });
                          }} sx={fieldSx} />
                      </TableCell>
                      {(['right', 'left', 'notes'] as const).map((col) => (
                        <TableCell key={col}>
                          <TextField fullWidth size="small" value={row[col]}
                            onChange={(e) => {
                              const rows = [...value.strength.rows];
                              rows[idx] = { ...row, [col]: e.target.value };
                              onChange({ ...value, strength: { ...value.strength, rows } });
                            }} sx={fieldSx} />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </ScrollTable>
            )}

            {section.key === 'coordination' && (
              <Box>
                <TriStateRadio isMobile={isMobile} label="Index-Nariz" value={value.coordination.index_nose}
                  onChange={(v) => onChange({ ...value, coordination: { ...value.coordination, index_nose: v } })} />
                <TriStateRadio isMobile={isMobile} label="Prono-Supino" value={value.coordination.prono_supino}
                  onChange={(v) => onChange({ ...value, coordination: { ...value.coordination, prono_supino: v } })} />
                <TriStateRadio isMobile={isMobile} label="LemoCot" value={value.coordination.lemocot}
                  onChange={(v) => onChange({ ...value, coordination: { ...value.coordination, lemocot: v } })} />
              </Box>
            )}

            {section.key === 'balance' && (
              <Grid container spacing={1.5}>
                {([
                  ['open_base', 'Base aberta — 30 s'],
                  ['romberg', 'Romberg — pés juntos'],
                  ['tandem', 'Tandem'],
                  ['unipodal_right', 'Unipodal D'],
                  ['unipodal_left', 'Unipodal E'],
                ] as const).map(([key, label]) => (
                  <Grid item xs={12} sm={6} key={key}>
                    <TextField fullWidth size="small" label={label} value={value.balance[key]}
                      onChange={(e) => onChange({ ...value, balance: { ...value.balance, [key]: e.target.value } })} sx={fieldSx} />
                  </Grid>
                ))}
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth size="small" label="TUG (segundos)" value={value.balance.tug_seconds}
                    onChange={(e) => onChange({ ...value, balance: { ...value.balance, tug_seconds: e.target.value } })} sx={fieldSx} />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" sx={{ color: '#64748b' }}>
                    Referência TUG: até 20 s = baixo risco; 20–29 s = risco moderado; &gt;30 s = alto risco.
                  </Typography>
                </Grid>
              </Grid>
            )}

            {section.key === 'gait' && (
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth size="small" label="Caminhada 4 m (segundos)" value={value.gait.walk_4m_seconds}
                    onChange={(e) => onChange({ ...value, gait: { ...value.gait, walk_4m_seconds: e.target.value } })} sx={fieldSx} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth size="small" label="Velocidade da marcha (m/s)" value={value.gait.gait_speed_ms}
                    onChange={(e) => onChange({ ...value, gait: { ...value.gait, gait_speed_ms: e.target.value } })} sx={fieldSx} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth size="small" label="Classificação" value={value.gait.classification}
                    onChange={(e) => onChange({ ...value, gait: { ...value.gait, classification: e.target.value } })} sx={fieldSx} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth multiline minRows={2} size="small" label="Análise da marcha" value={value.gait.analysis}
                    onChange={(e) => onChange({ ...value, gait: { ...value.gait, analysis: e.target.value } })} sx={fieldSx} />
                </Grid>
              </Grid>
            )}

            {section.key === 'functional_tests' && (
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" label="Sentar e levantar — 5 repetições (segundos)" value={value.functional_tests.sit_stand_5x_seconds}
                    onChange={(e) => onChange({ ...value, functional_tests: { ...value.functional_tests, sit_stand_5x_seconds: e.target.value } })} sx={fieldSx} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" label="Teste do degrau — 2 min (repetições)" value={value.functional_tests.step_2min_reps}
                    onChange={(e) => onChange({ ...value, functional_tests: { ...value.functional_tests, step_2min_reps: e.target.value } })} sx={fieldSx} />
                </Grid>
              </Grid>
            )}

            {section.key === 'posture' && (
              <PosturalAssessmentSection
                patientId={patientId}
                readOnly={readOnly}
                value={value.posture}
                onChange={(posture) => applyChange({ ...value, posture })}
              />
            )}

            {section.key === 'impression' && (
              <Grid container spacing={1.5}>
                {([
                  ['main_alterations', 'Principais alterações encontradas'],
                  ['functional_limitations', 'Limitações funcionais identificadas'],
                  ['risk_factors', 'Fatores de risco / pontos de atenção'],
                ] as const).map(([key, label]) => (
                  <Grid item xs={12} key={key}>
                    <TextField fullWidth multiline minRows={2} size="small" label={label} value={value.impression[key]}
                      onChange={(e) => onChange({ ...value, impression: { ...value.impression, [key]: e.target.value } })} sx={fieldSx} />
                  </Grid>
                ))}
              </Grid>
            )}

            {section.key === 'therapeutic_goals' && (
              <Grid container spacing={1.5}>
                {([
                  ['short_term', 'Curto prazo'],
                  ['medium_term', 'Médio prazo'],
                  ['long_term', 'Longo prazo'],
                  ['functional_goals', 'Metas funcionais'],
                ] as const).map(([key, label]) => (
                  <Grid item xs={12} sm={6} key={key}>
                    <TextField fullWidth multiline minRows={2} size="small" label={label} value={value.therapeutic_goals[key]}
                      onChange={(e) => onChange({ ...value, therapeutic_goals: { ...value.therapeutic_goals, [key]: e.target.value } })} sx={fieldSx} />
                  </Grid>
                ))}
              </Grid>
            )}

            {section.key === 'therapeutic_plan' && (
              <Grid container spacing={1.5}>
                <Grid item xs={12}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569' }}>Modalidade / conduta</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {PHYSIO_THERAPY_MODALITIES.map((m) => (
                      <FormControlLabel key={m.key}
                        control={<Checkbox size="small" checked={value.therapeutic_plan.modalities.includes(m.key)}
                          onChange={() => onChange({ ...value, therapeutic_plan: { ...value.therapeutic_plan, modalities: toggleArray(value.therapeutic_plan.modalities, m.key) } })} />}
                        label={<Typography variant="caption">{m.label}</Typography>}
                      />
                    ))}
                  </Box>
                  {value.therapeutic_plan.modalities.includes('other') && (
                    <TextField fullWidth size="small" label="Outra modalidade" sx={{ mt: 1, ...fieldSx }}
                      value={value.therapeutic_plan.modality_other}
                      onChange={(e) => onChange({ ...value, therapeutic_plan: { ...value.therapeutic_plan, modality_other: e.target.value } })} />
                  )}
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth size="small" label="Frequência (vezes/semana)" value={value.therapeutic_plan.frequency_per_week}
                    onChange={(e) => onChange({ ...value, therapeutic_plan: { ...value.therapeutic_plan, frequency_per_week: e.target.value } })} sx={fieldSx} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth multiline minRows={2} size="small" label="Orientações" value={value.therapeutic_plan.guidance}
                    onChange={(e) => onChange({ ...value, therapeutic_plan: { ...value.therapeutic_plan, guidance: e.target.value } })} sx={fieldSx} />
                </Grid>
              </Grid>
            )}
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
}

function ReEvaluationForm({
  value,
  onChange,
  readOnly = false,
  isMobile,
}: {
  value: PhysioReEvaluationResponses;
  onChange: (next: PhysioReEvaluationResponses) => void;
  readOnly?: boolean;
  isMobile: boolean;
}) {
  const applyChange = (next: PhysioReEvaluationResponses) => {
    if (!readOnly) onChange(next);
  };

  const toggleConduct = (key: string) => {
    const conduct = value.conclusion.conduct.includes(key)
      ? value.conclusion.conduct.filter((k) => k !== key)
      : [...value.conclusion.conduct, key];
    applyChange({ ...value, conclusion: { ...value.conclusion, conduct } });
  };

  const setReEvalId = (patch: Partial<PhysioReEvaluationResponses['identification']>) => {
    applyChange(applyReEvalAnthropometryCurrent({
      ...value,
      identification: { ...value.identification, ...patch },
    }));
  };

  const renderComparisonTable = (
    rows: PhysioReEvaluationResponses['comparison'],
    onRowsChange: (rows: PhysioReEvaluationResponses['comparison']) => void,
  ) => (
    <ScrollTable minWidth={720}>
      <TableHead>
        <TableRow>
          <TableCell>Indicador</TableCell>
          <TableCell>Inicial</TableCell>
          <TableCell>Atual</TableCell>
          <TableCell>Evolução (Δ automático)</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row, idx) => (
          <TableRow key={idx}>
            <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', minWidth: 140 }}>{row.indicator}</TableCell>
            {(['initial', 'current'] as const).map((col) => (
              <TableCell key={col}>
                <TextField fullWidth size="small" value={row[col]}
                  onChange={(e) => {
                    const next = [...rows];
                    next[idx] = { ...row, [col]: e.target.value };
                    onRowsChange(recomputeComparisonRows(next));
                  }} sx={fieldSx} />
              </TableCell>
            ))}
            <TableCell>
              <TextField fullWidth size="small" value={row.evolution} InputProps={{ readOnly: true }} sx={fieldSx} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </ScrollTable>
  );

  return (
    <Box sx={{ ...(readOnly ? readOnlyContainerSx : {}), ...mobileFormContainerSx(isMobile) }}>
      {PHYSIO_REEVAL_SECTIONS.map((section) => (
        <Accordion key={section.key} defaultExpanded={section.key === 'identification'} sx={sectionAccordionSx(isMobile)}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={sectionSummarySx}>
            <Typography sx={{ fontWeight: 700, color: PATIENT_PANEL_THEME.sectionTitle, fontSize: '0.9rem' }}>
              {section.title}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={sectionDetailsSx(isMobile)}>
            {section.key === 'identification' && (
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" label="Período de tratamento" value={value.identification.treatment_period}
                    onChange={(e) => onChange({ ...value, identification: { ...value.identification, treatment_period: e.target.value } })} sx={fieldSx} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl>
                    <FormLabel sx={{ fontSize: '0.75rem' }}>Adesão ao tratamento</FormLabel>
                    <RadioGroup row={!isMobile} value={value.identification.adherence}
                      onChange={(e) => applyChange({ ...value, identification: { ...value.identification, adherence: e.target.value as PhysioReEvaluationResponses['identification']['adherence'] } })}>
                      <FormControlLabel value="good" control={<Radio size="small" />} label="Boa" />
                      <FormControlLabel value="regular" control={<Radio size="small" />} label="Regular" />
                      <FormControlLabel value="low" control={<Radio size="small" />} label="Baixa" />
                    </RadioGroup>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569' }}>
                    Antropometria atual (atualiza automaticamente a tabela comparativa)
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth size="small" label="Peso atual (kg)" value={value.identification.current_weight_kg}
                    onChange={(e) => setReEvalId({ current_weight_kg: e.target.value })} sx={fieldSx} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth size="small" label="Altura atual (m)" value={value.identification.current_height_m}
                    onChange={(e) => setReEvalId({ current_height_m: e.target.value })} sx={fieldSx} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth size="small" label="IMC atual" value={value.identification.current_imc}
                    InputProps={{ readOnly: true }} sx={fieldSx} />
                </Grid>
              </Grid>
            )}

            {section.key === 'comparison' && renderComparisonTable(value.comparison, (rows) => onChange({ ...value, comparison: rows }))}

            {section.key === 'functional_evolution' && (
              <ScrollTable minWidth={760}>
                <TableHead>
                  <TableRow>
                    <TableCell>Objetivo / atividade</TableCell>
                    <TableCell>Avaliação inicial</TableCell>
                    <TableCell>Reavaliação</TableCell>
                    <TableCell>Situação</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {value.functional_evolution.map((row, idx) => (
                    <TableRow key={idx}>
                      <TableCell>
                        <TextField fullWidth size="small" value={row.goal_activity}
                          onChange={(e) => {
                            const functional_evolution = [...value.functional_evolution];
                            functional_evolution[idx] = { ...row, goal_activity: e.target.value };
                            applyChange({ ...value, functional_evolution });
                          }} sx={fieldSx} />
                      </TableCell>
                      <TableCell>
                        <TextField fullWidth size="small" value={row.initial_assessment}
                          onChange={(e) => {
                            const functional_evolution = [...value.functional_evolution];
                            functional_evolution[idx] = { ...row, initial_assessment: e.target.value };
                            applyChange({ ...value, functional_evolution });
                          }} sx={fieldSx} />
                      </TableCell>
                      <TableCell>
                        <TextField fullWidth size="small" value={row.reevaluation}
                          onChange={(e) => {
                            const functional_evolution = [...value.functional_evolution];
                            functional_evolution[idx] = { ...row, reevaluation: e.target.value };
                            applyChange({ ...value, functional_evolution });
                          }} sx={fieldSx} />
                      </TableCell>
                      <TableCell sx={{ minWidth: isMobile ? 160 : 120 }}>
                        <RadioGroup value={row.status} onChange={(e) => {
                          const functional_evolution = [...value.functional_evolution];
                          functional_evolution[idx] = { ...row, status: e.target.value as typeof row.status };
                          applyChange({ ...value, functional_evolution });
                        }}>
                          <FormControlLabel value="achieved" control={<Radio size="small" />} label="Alcançado" />
                          <FormControlLabel value="partial" control={<Radio size="small" />} label="Parcial" />
                          <FormControlLabel value="not_achieved" control={<Radio size="small" />} label="Não alcançado" />
                        </RadioGroup>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </ScrollTable>
            )}

            {section.key === 'anthropometry_comparison' && renderComparisonTable(
              value.anthropometry_comparison,
              (rows) => onChange({ ...value, anthropometry_comparison: rows }),
            )}

            {section.key === 'conclusion' && (
              <Grid container spacing={1.5}>
                <Grid item xs={12}>
                  <FormControl>
                    <FormLabel sx={{ fontSize: '0.75rem' }}>Evolução clínica</FormLabel>
                    <RadioGroup row={!isMobile} value={value.conclusion.clinical_evolution}
                      onChange={(e) => applyChange({ ...value, conclusion: { ...value.conclusion, clinical_evolution: e.target.value as PhysioReEvaluationResponses['conclusion']['clinical_evolution'] } })}>
                      <FormControlLabel value="excellent" control={<Radio size="small" />} label="Excelente" />
                      <FormControlLabel value="good" control={<Radio size="small" />} label="Boa" />
                      <FormControlLabel value="partial" control={<Radio size="small" />} label="Parcial" />
                      <FormControlLabel value="none" control={<Radio size="small" />} label="Sem evolução" />
                      <FormControlLabel value="worse" control={<Radio size="small" />} label="Piora" />
                    </RadioGroup>
                  </FormControl>
                </Grid>
                {([
                  ['main_gains', 'Principais ganhos observados'],
                  ['remaining_limitations', 'Limitações que permanecem'],
                  ['achieved_goals', 'Objetivos atingidos'],
                  ['new_goals', 'Novos objetivos'],
                ] as const).map(([key, label]) => (
                  <Grid item xs={12} key={key}>
                    <TextField fullWidth multiline minRows={2} size="small" label={label} value={value.conclusion[key]}
                      onChange={(e) => onChange({ ...value, conclusion: { ...value.conclusion, [key]: e.target.value } })} sx={fieldSx} />
                  </Grid>
                ))}
                <Grid item xs={12}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569' }}>Conduta a partir da reavaliação</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {PHYSIO_CONDUCT_OPTIONS.map((c) => (
                      <FormControlLabel key={c.key}
                        control={<Checkbox size="small" checked={value.conclusion.conduct.includes(c.key)} onChange={() => toggleConduct(c.key)} />}
                        label={<Typography variant="caption">{c.label}</Typography>}
                      />
                    ))}
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth multiline minRows={2} size="small" label="Observações" value={value.conclusion.notes}
                    onChange={(e) => onChange({ ...value, conclusion: { ...value.conclusion, notes: e.target.value } })} sx={fieldSx} />
                </Grid>
              </Grid>
            )}
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
}

export interface PhysiotherapyAssessmentFormProps {
  assessmentType: PhysiotherapyAssessmentType;
  responses: PhysioInitialResponses | PhysioReEvaluationResponses;
  onChange: (responses: PhysioInitialResponses | PhysioReEvaluationResponses) => void;
  patientId?: string;
  readOnly?: boolean;
}

const PhysiotherapyAssessmentForm: React.FC<PhysiotherapyAssessmentFormProps> = ({
  assessmentType,
  responses,
  onChange,
  patientId,
  readOnly = false,
}) => {
  const { isMobile } = useClinicPageBreakpoints();

  if (assessmentType === 're_evaluation') {
    return (
      <ReEvaluationForm
        value={responses as PhysioReEvaluationResponses}
        onChange={onChange as (v: PhysioReEvaluationResponses) => void}
        readOnly={readOnly}
        isMobile={isMobile}
      />
    );
  }
  return (
    <InitialForm
      value={responses as PhysioInitialResponses}
      onChange={onChange as (v: PhysioInitialResponses) => void}
      patientId={patientId}
      readOnly={readOnly}
      isMobile={isMobile}
    />
  );
};

export default PhysiotherapyAssessmentForm;
